/**
 * 出発時刻の提案。
 *
 * 同じ経路でも、出発が数十分ずれるだけで浴びる量は変わる。太陽高度が動くのと、
 * 雲の予報（Open-Meteoの uv_index は雲込み）が時間で変わるためである。
 * ここでは経路を固定したまま出発時刻だけをずらして被曝を積分し直し、
 * 予報の範囲で最も少ない時刻を返す。
 *
 * 経路そのものは再探索しない（太陽方位が変われば最適経路も厳密には変わるが、
 * 1本あたり約1秒かかる経路探索を窓の数だけ回すと実用にならないため）。
 * 提案時刻での再探索は呼び出し側が必要に応じて行う。
 */
import { F_POSE, UVI_TO_WM2, WALK_SPEED_M_PER_MIN } from "./constants.js";
import { solarPosition } from "./solar.js";
import { sector, TerrainTile } from "./terrain.js";
import { fdUv } from "./wx.js";
import type { LatLon, Ms, WxMinute } from "./types.js";

/** 出発時刻の評価に必要な気象（クライアントが予報から供給する） */
export type WxSample = Pick<WxMinute, "uvi" | "uviClearSky" | "fdBb">;
export type WxLookup = (ms: Ms) => WxSample;

export interface RouteExposure {
  /** 経路全体の被曝量 [SED]（日焼け止めなし） */
  sed: number;
  /** 日陰率 0..1 */
  shadeShare: number;
  /** 所要時間 [min] */
  timeMin: number;
}

function metersBetween(a: LatLon, b: LatLon): number {
  const mLat = 111320;
  const mLon = 111320 * Math.cos((a.lat * Math.PI) / 180);
  return Math.hypot((a.lat - b.lat) * mLat, (a.lon - b.lon) * mLon);
}

/**
 * 経路上の被曝を積分する。歩いている間に太陽が動くことも織り込む
 * （各区間の中点で、その区間を通過する時刻の太陽位置・気象を使う）。
 */
export function integrateRouteExposure(
  tile: TerrainTile,
  path: LatLon[],
  departMs: Ms,
  wxAt: WxLookup,
): RouteExposure {
  let sed = 0;
  let elapsedMin = 0;
  let shadeMin = 0;

  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]!;
    const b = path[i]!;
    const legMin = metersBetween(a, b) / WALK_SPEED_M_PER_MIN;
    if (legMin <= 0) continue;
    const mid: LatLon = { lat: (a.lat + b.lat) / 2, lon: (a.lon + b.lon) / 2 };
    const at = departMs + (elapsedMin + legMin / 2) * 60000;

    const sun = solarPosition(at, mid);
    const wx = wxAt(at);
    const cell = tile.lookup(mid);
    const horizon = cell ? (cell.horizonDeg[sector(sun.azDeg)] ?? 0) : 0;
    const svf = cell ? cell.svf : 1;
    const visible = sun.elevDeg > horizon;

    const fd = fdUv(wx.fdBb);
    const eOpen = UVI_TO_WM2 * Math.max(0, wx.uvi);
    const eSkin = (eOpen * (1 - fd) * (visible ? 1 : 0) + eOpen * fd * svf) * F_POSE;
    sed += (eSkin * legMin * 60) / 100;

    elapsedMin += legMin;
    if (!visible) shadeMin += legMin;
  }

  return {
    sed,
    shadeShare: elapsedMin > 0 ? shadeMin / elapsedMin : 0,
    timeMin: elapsedMin,
  };
}

export interface DepartureOption extends RouteExposure {
  departMs: Ms;
  /** 基準時刻からのずれ [分]（負なら早める） */
  deltaMin: number;
}

/** 提案しない理由が分かるよう、候補一覧も返す */
export interface DepartureAdvice {
  base: DepartureOption;
  best: DepartureOption;
  options: DepartureOption[];
  /** 基準に対する削減率 0..1 */
  reductionRatio: number;
  /** 提案する価値があるか（しきい値超え） */
  worthShifting: boolean;
  /** 主な理由。文言はUI側でテンプレ合成する（§9.1と同じく決定的） */
  reason: "cloud" | "sun_low" | "none";
}

export interface DepartureAdviceOptions {
  tile: TerrainTile;
  path: LatLon[];
  baseDepartMs: Ms;
  wxAt: WxLookup;
  /** 前後に何分ずらして探すか（既定30分） */
  windowMin?: number;
  /** 刻み（既定5分） */
  stepMin?: number;
  /** これ以上減るなら提案する（既定15%） */
  minGainRatio?: number;
  /** 微小な差で提案しないための絶対量の下限 [SED]（既定0.02） */
  minGainSed?: number;
}

/**
 * 出発時刻をずらす提案。窓内で最も被曝の少ない時刻を探す。
 * 「早めるほど良い」とは限らない（朝は遅らせるほど強くなるが、夕方は逆）ため、
 * 前後どちらもフラットに評価する。
 */
export function adviseDeparture(opts: DepartureAdviceOptions): DepartureAdvice {
  const windowMin = opts.windowMin ?? 30;
  const stepMin = opts.stepMin ?? 5;
  const minGainRatio = opts.minGainRatio ?? 0.15;
  const minGainSed = opts.minGainSed ?? 0.02;

  const evaluate = (deltaMin: number): DepartureOption => {
    const departMs = opts.baseDepartMs + deltaMin * 60000;
    return { ...integrateRouteExposure(opts.tile, opts.path, departMs, opts.wxAt), departMs, deltaMin };
  };

  const base = evaluate(0);
  const options: DepartureOption[] = [];
  for (let d = -windowMin; d <= windowMin; d += stepMin) {
    options.push(d === 0 ? base : evaluate(d));
  }

  let best = base;
  for (const o of options) {
    // 同じ被曝なら基準に近い方を選ぶ（無駄に時刻をずらさない）
    if (o.sed < best.sed - 1e-12 || (Math.abs(o.sed - best.sed) < 1e-12 && Math.abs(o.deltaMin) < Math.abs(best.deltaMin))) {
      best = o;
    }
  }

  const gain = base.sed - best.sed;
  const reductionRatio = base.sed > 0 ? gain / base.sed : 0;
  const worthShifting = best.deltaMin !== 0 && reductionRatio >= minGainRatio && gain >= minGainSed;

  return {
    base,
    best,
    options,
    reductionRatio,
    worthShifting,
    reason: worthShifting ? reasonFor(opts, base, best) : "none",
  };
}

/**
 * 減る理由を判定する。雲の増加（CMFの低下）が主因か、太陽高度の低さが主因か。
 * 経路の中点・所要時間の中間時刻で代表評価する。
 */
function reasonFor(
  opts: DepartureAdviceOptions,
  base: DepartureOption,
  best: DepartureOption,
): "cloud" | "sun_low" {
  const mid = opts.path[Math.floor(opts.path.length / 2)] ?? opts.path[0]!;
  const at = (o: DepartureOption) => o.departMs + (o.timeMin / 2) * 60000;
  const cmf = (s: WxSample) => (s.uviClearSky > 0 ? s.uvi / s.uviClearSky : 1);

  const cmfBase = cmf(opts.wxAt(at(base)));
  const cmfBest = cmf(opts.wxAt(at(best)));
  if (cmfBest < cmfBase - 0.1) return "cloud";

  const elevBase = solarPosition(at(base), mid).elevDeg;
  const elevBest = solarPosition(at(best), mid).elevDeg;
  return elevBest < elevBase ? "sun_low" : "cloud";
}

/** 提案の文言（付録Aのテンプレ方式に合わせ、LLMを使わず決定的に合成する） */
export function departureAdviceMessage(advice: DepartureAdvice): string | null {
  if (!advice.worthShifting) return null;
  const d = advice.best.deltaMin;
  const when = d < 0 ? `${-d}分早く` : `${d}分遅く`;
  const pct = Math.round(advice.reductionRatio * 100);
  const why =
    advice.reason === "cloud" ? "雲が出る予報なので" : "日差しがまだ弱い時間帯なので";
  return `${why}、${when}出ると日焼けを約${pct}%減らせます`;
}
